import { useEffect, useState } from 'react';
import {
  ChiTieuNganSach,
  ChiTieuNganSachInput,
  ChiTieuNganSachResponse
} from '@/types/schemas/chi-tieu-ngan-sach.type';
import { useAuth } from '@/contexts/auth-context';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

interface UseChiTieuNganSachReturn {
  chiTieuNganSachs: ChiTieuNganSach[];
  isLoading: boolean;
  addChiTieuNganSach: (newChiTieuNganSach: ChiTieuNganSachInput) => Promise<ChiTieuNganSach>;
  updateChiTieuNganSach: (uuid: string, updatedChiTieuNganSach: ChiTieuNganSachInput) => Promise<ChiTieuNganSach>;
  deleteChiTieuNganSach: (uuid: string) => Promise<void>;
  refreshChiTieuNganSachs: () => Promise<void>;
}

/**
 * Hook for managing ChiTieuNganSach (Budget Target) data
 *
 * This hook provides functions to fetch, create, update, and delete budget targets.
 */
export const useChiTieuNganSach = (): UseChiTieuNganSachReturn => {
  const [chiTieuNganSachs, setChiTieuNganSachs] = useState<ChiTieuNganSach[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { entity } = useAuth();

  /**
   * Fetch all budget targets
   */
  const fetchChiTieuNganSachs = async (): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<ChiTieuNganSachResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CHI_TIEU_NGAN_SACH}/`
      );
      setChiTieuNganSachs(response.data.results);
    } catch (error) {
      console.error('Error fetching budget targets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Add a new budget target
   */
  const addChiTieuNganSach = async (newChiTieuNganSach: ChiTieuNganSachInput): Promise<ChiTieuNganSach> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.post<ChiTieuNganSach>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CHI_TIEU_NGAN_SACH}/`,
        newChiTieuNganSach
      );
      setChiTieuNganSachs(prev => [...prev, response.data]);
      return response.data;
    } catch (error) {
      console.error('Error adding budget target:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update an existing budget target
   */
  const updateChiTieuNganSach = async (
    uuid: string,
    updatedChiTieuNganSach: ChiTieuNganSachInput
  ): Promise<ChiTieuNganSach> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.put<ChiTieuNganSach>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CHI_TIEU_NGAN_SACH}/${uuid}/`,
        updatedChiTieuNganSach
      );
      setChiTieuNganSachs(prev => prev.map(item => (item.uuid === uuid ? response.data : item)));
      return response.data;
    } catch (error) {
      console.error('Error updating budget target:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a budget target
   */
  const deleteChiTieuNganSach = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.CHI_TIEU_NGAN_SACH}/${uuid}/`);
      setChiTieuNganSachs(prev => prev.filter(item => item.uuid !== uuid));
      await fetchChiTieuNganSachs();
    } catch (error) {
      console.error('Error deleting budget target:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch budget targets on component mount or when entity changes
  useEffect(() => {
    if (entity?.slug) {
      fetchChiTieuNganSachs();
    }
  }, [entity?.slug]);

  return {
    chiTieuNganSachs,
    isLoading,
    addChiTieuNganSach,
    updateChiTieuNganSach,
    deleteChiTieuNganSach,
    refreshChiTieuNganSachs: fetchChiTieuNganSachs
  };
};
